/**
 * MyBatis /common/select · /common/save 헬퍼 (Phase-1 Store persistence)
 */
import api from 'services/api';

/**
 * @param {string} queryId - 예: 'BdAcctClass.selectAcctClassList'
 * @param {Record<string, unknown>} params - tenantCode, fiscalYear 등 (JWT sCompCd 와 병행)
 * @param {string} [resultKey='gridList']
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function commonSelect(queryId, params = {}, resultKey = 'gridList') {
  const res = await api.post('/common/select', {
    ...params,
    querySet: JSON.stringify({ [resultKey]: queryId }),
  });
  const data = res?.data || {};
  const list = data[resultKey];
  return Array.isArray(list) ? list : [];
}

/**
 * 그리드 A/U/D 행만 저장.
 * @param {[string, string, string]} saveQuery - [insertId, updateId, deleteId]
 * @param {Array} rows - mod 가 A|U|D 인 행
 * @param {Record<string, unknown>} [addValue]
 */
export async function commonSaveList(saveQuery, rows, addValue = {}) {
  const dirty = sortDirtyByMod(
    (rows || []).filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'))
  );
  if (!dirty.length) {
    return { Msg: 'success', skipped: true };
  }
  const body = {
    logicObj: JSON.stringify({
      logic: [{ actionType: 'list', query: saveQuery, data: dirty }],
    }),
  };
  if (addValue && Object.keys(addValue).length > 0) {
    body.addValue = JSON.stringify(addValue);
  }
  const res = await api.post('/common/save', body);
  const data = res?.data || {};
  if (String(data.Msg || '').toLowerCase() !== 'success') {
    throw new Error(data.Msg || '저장 실패');
  }
  return data;
}

/**
 * 단건 row action (확정 플래그 등)
 * @param {string} queryId
 * @param {Record<string, unknown>} data
 * @param {Record<string, unknown>} [addValue]
 */
export async function commonSaveRow(queryId, data = {}, addValue = {}) {
  const body = {
    logicObj: JSON.stringify({
      logic: [{ actionType: 'row', query: queryId, data }],
    }),
  };
  if (addValue && Object.keys(addValue).length > 0) {
    body.addValue = JSON.stringify(addValue);
  }
  const res = await api.post('/common/save', body);
  const dataRes = res?.data || {};
  if (String(dataRes.Msg || '').toLowerCase() !== 'success') {
    throw new Error(dataRes.Msg || '저장 실패');
  }
  return dataRes;
}

/** 캐시에 없는 동기 옵션용 — 빈 배열이면 fallback */
export function cacheGet(map, key) {
  if (!key) return null;
  return map.has(key) ? map.get(key) : null;
}

export function cacheSet(map, key, rows) {
  if (!key) return;
  map.set(key, Array.isArray(rows) ? rows : []);
}

/** JSON 컬럼·문자열 → 배열 */
export function parseJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** JSON 컬럼·문자열 → 객체 */
export function parseJsonObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (value == null || value === '') return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * prev/next 전체 치환용 A/U/D 산출 (마스터 PoC 저장)
 * @param {Array} prev
 * @param {Array} next - mod==='D' 제외한 유지 행
 * @param {string} uidKey
 * @param {(row: object) => string} [ensureUid]
 */
export function diffRowsByUid(prev = [], next = [], uidKey, ensureUid) {
  const activeNext = (next || []).filter((r) => r && r.mod !== 'D');
  const prevMap = new Map(
    (prev || []).filter((r) => r && r[uidKey]).map((r) => [String(r[uidKey]), r])
  );
  const nextMap = new Map();
  const dirty = [];

  for (const row of activeNext) {
    let uid = String(row[uidKey] || '').trim();
    if (!uid && typeof ensureUid === 'function') {
      uid = String(ensureUid(row) || '').trim();
    }
    if (!uid) continue;
    const prepared = { ...row, [uidKey]: uid };
    nextMap.set(uid, prepared);
    if (!prevMap.has(uid)) {
      dirty.push({ ...prepared, mod: 'A' });
    } else {
      dirty.push({ ...prepared, mod: row.mod === 'A' ? 'A' : 'U' });
    }
  }

  for (const [uid, row] of prevMap) {
    if (!nextMap.has(uid)) {
      dirty.push({ ...row, mod: 'D' });
    }
  }
  return dirty;
}

/** /common/save 행 순서 — FK·UK 충돌 방지용 D → U → A */
export function sortDirtyByMod(rows = []) {
  const rank = { D: 0, U: 1, A: 2 };
  return [...(rows || [])].sort(
    (a, b) => (rank[a?.mod] ?? 9) - (rank[b?.mod] ?? 9)
  );
}
